import { PLACEHOLDER_RE, findPlaceholderRefs, isBuiltin, variablesForAnyCollection, type TestStep, type VariableProblem, type VariableView } from '@1qa/shared';

/** `{{KEY}}` text for a variable name. */
export const placeholderFor = (key: string) => `{{${key}}}`;

export interface TextSegment {
  text: string;
  /** Set when this segment is a {{...}} placeholder; holds the variable name. */
  placeholder?: string;
  /** The value written in the test ({{NAME|written}}), used when the test data has no NAME. */
  written?: string;
}

/** Splits text into plain and {{placeholder}} segments (for highlighting in the preview). */
export function splitPlaceholders(text: string): TextSegment[] {
  const out: TextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(new RegExp(PLACEHOLDER_RE.source, 'g'))) {
    const at = m.index ?? 0;
    if (at > last) out.push({ text: text.slice(last, at) });
    out.push({ text: m[0], placeholder: m[1], ...(m[2] !== undefined ? { written: m[2].trim() } : {}) });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** Replaces the selection [start, end) with `insert`; returns the new value and caret position. */
export function insertAt(value: string, start: number, end: number, insert: string): { value: string; cursor: number } {
  const s = Math.max(0, Math.min(start, value.length));
  const e = Math.max(s, Math.min(end, value.length));
  return { value: value.slice(0, s) + insert + value.slice(e), cursor: s + insert.length };
}

/**
 * key -> isSecret for the variables a test can use: the default values, or with any test data
 * collection (same rule as the API when a test is saved).
 */
export function effectiveVariables(views: VariableView[]): Map<string, boolean> {
  return variablesForAnyCollection(views.map((v) => ({ key: v.key, isSecret: v.isSecret, projectId: v.scope === 'PROJECT' ? 'project' : null, collectionId: v.collectionId ?? null })));
}

/** Groups variable problems by step id. */
export function problemsByStep(problems: VariableProblem[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const p of problems) map.set(p.stepId, [...(map.get(p.stepId) ?? []), p.message]);
  return map;
}

/** Reads VariableProblem[] from an INVALID_VARIABLES error's details, tolerating unexpected shapes. */
export function variableProblemsFrom(details: unknown): VariableProblem[] {
  const list = Array.isArray(details) ? details : Array.isArray((details as { problems?: unknown })?.problems) ? (details as { problems: unknown[] }).problems : [];
  return list.filter((p): p is VariableProblem => !!p && typeof p === 'object' && typeof (p as VariableProblem).stepId === 'string' && typeof (p as VariableProblem).message === 'string');
}

export interface CoverageGap {
  name: string;
  /** Tests that use the name (each listed once). */
  tests: string[];
}

/**
 * What the project's tests need that a run with `collectionId` (null: no collection) cannot fill from
 * the collection or the default test data. Collections are independent: only names the tests use
 * count, never what other collections contain, and a name the default test data has is fine.
 *  - `missing`: some use has no written value, so those tests stop with an error.
 *  - `written`: every use has a written value ({{NAME|text}}), which the run uses instead.
 */
export function collectionCoverage(
  tests: { name: string; steps: TestStep[] }[],
  variables: Pick<VariableView, 'key' | 'collectionId'>[],
  collectionId: string | null,
): { missing: CoverageGap[]; written: CoverageGap[] } {
  const provided = new Set(variables.filter((v) => !v.collectionId || v.collectionId === collectionId).map((v) => v.key));
  const uses = new Map<string, { tests: Set<string>; withoutWritten: Set<string> }>();
  for (const t of tests) {
    for (const step of t.steps) {
      for (const ref of findPlaceholderRefs(step.value)) {
        if (isBuiltin(ref.name) || provided.has(ref.name)) continue;
        const u = uses.get(ref.name) ?? { tests: new Set<string>(), withoutWritten: new Set<string>() };
        u.tests.add(t.name);
        if (ref.written === undefined) u.withoutWritten.add(t.name);
        uses.set(ref.name, u);
      }
    }
  }
  const missing: CoverageGap[] = [];
  const written: CoverageGap[] = [];
  for (const [name, u] of [...uses].sort(([a], [b]) => a.localeCompare(b))) {
    if (u.withoutWritten.size) missing.push({ name, tests: [...u.withoutWritten] });
    else written.push({ name, tests: [...u.tests] });
  }
  return { missing, written };
}
